import { format, parseISO } from 'date-fns';
import { FC, useId, useState } from 'react';
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';
import { DataRoomEngagement, OTHER_ENGAGEMENT_SERIES_KEY } from 'shared';

import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';

// Enough for the five lines §4 allows plus the folded one, in the module's own palette: each
// line and the pale tint its area fades from.
const SERIES_COLOURS = [
  { line: '#2565C8', tint: '#F4F8FF' },
  { line: '#12B76A', tint: '#F6FEF9' },
  { line: '#F79009', tint: '#FFFAEB' },
  { line: '#7F56D9', tint: '#F9F5FF' },
  { line: '#0E9384', tint: '#F0FDF9' },
  { line: '#98A2B3', tint: '#F9FAFB' },
];

const AXIS_COLOUR = '#667085';
const TICK_COLOUR = '#475467';
const ACTIVE_TICK_COLOUR = '#1151B4';

const seriesKey = (dataRoomId: null | string): string => dataRoomId ?? OTHER_ENGAGEMENT_SERIES_KEY;

const seriesColour = (index: number) => SERIES_COLOURS[index % SERIES_COLOURS.length];

// The design centres the counts in a 38px gutter that keeps 16px clear of the axis.
const Y_TICK_CENTRE = 11;

// Points are UTC days as plain dates. `parseISO` reads one as that calendar day; `new Date` would
// read it as UTC midnight and show the day before anywhere west of UTC.
const DateTick: FC<{
  activeDate?: string;
  payload?: { value: string };
  x?: number;
  y?: number;
}> = ({ activeDate, payload, x, y }) =>
  payload ? (
    <text
      fill={payload.value === activeDate ? ACTIVE_TICK_COLOUR : TICK_COLOUR}
      fontSize={10}
      fontWeight={500}
      textAnchor="middle"
      x={x}
      y={(y ?? 0) + 12}
    >
      {format(parseISO(payload.value), 'dd/MM')}
    </text>
  ) : null;

const CountTick: FC<{ payload?: { value: number }; y?: number }> = ({ payload, y }) =>
  payload ? (
    <text
      dy={4}
      fill={TICK_COLOUR}
      fontSize={10}
      fontWeight={500}
      textAnchor="middle"
      x={Y_TICK_CENTRE}
      y={y}
    >
      {payload.value}
    </text>
  ) : null;

const EngagementTooltip: FC<TooltipProps<number, string>> = ({ active, label, payload }) => {
  if (!active || !payload?.length) return null;

  return (
    <div className="flex w-[150px] flex-col gap-1 rounded-lg bg-white px-1 pb-1 pt-2 shadow-[0px_-4px_16px_0px_rgba(16,24,40,0.04),0px_24px_48px_0px_rgba(16,24,40,0.18)]">
      <span className="px-3 text-sm font-[550] leading-5 text-gray-700">
        {format(parseISO(String(label)), 'dd/MM/yyyy')}
      </span>
      <div className="flex flex-col">
        {payload.map(({ color, dataKey, name, value }) => (
          <div
            className="flex items-center border-b border-gray-50 last:border-b-0"
            key={String(dataKey)}
          >
            <span
              className="w-[85px] shrink-0 truncate px-3 py-1 text-label-sm font-medium"
              style={{ color }}
            >
              {name}:
            </span>
            <span className="flex-1 px-4 py-1 text-center text-label-sm font-medium text-[#172335]">
              {value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * 20.1.1 §4: one line per data room with at least one View in the window, daily counts, Views only.
 *
 * Every day of the window is a point even when nothing happened, so a quiet Tuesday reads as a
 * zero rather than as a line that jumps over it.
 */
export const EngagementChart: FC<{ engagement: DataRoomEngagement }> = ({ engagement }) => {
  const [activeDate, setActiveDate] = useState<string>();
  // Gradient ids are document-wide; the chart's own prefix keeps two charts from sharing one.
  const gradientPrefix = `engagement-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;

  if (!engagement.series.length) {
    return (
      <div className="flex h-[220px] items-center justify-center text-sm font-[450] text-gray-400">
        <AppFormattedMessage id="NO_ENGAGEMENT_YET" />
      </div>
    );
  }

  const data = engagement.points.map(({ date, views }) => ({
    date,
    ...Object.fromEntries(
      engagement.series.map(({ dataRoomId }) => {
        const key = seriesKey(dataRoomId);

        return [key, views[key] ?? 0];
      }),
    ),
  }));

  return (
    <ResponsiveContainer height={343} width="100%">
      <AreaChart
        data={data}
        margin={{ bottom: 0, left: 0, right: 8, top: 16 }}
        onMouseLeave={() => setActiveDate(undefined)}
        onMouseMove={({ activeLabel }) => setActiveDate(activeLabel)}
      >
        <defs>
          {engagement.series.map(({ dataRoomId }, index) => (
            <linearGradient
              id={`${gradientPrefix}-${seriesKey(dataRoomId)}`}
              key={seriesKey(dataRoomId)}
              x1="0"
              x2="0"
              y1="0"
              y2="1"
            >
              <stop offset="0%" stopColor={seriesColour(index).tint} />
              <stop offset="50%" stopColor={seriesColour(index).tint} stopOpacity={0} />
            </linearGradient>
          ))}
        </defs>
        <XAxis
          axisLine={{ stroke: AXIS_COLOUR }}
          dataKey="date"
          height={32}
          minTickGap={24}
          tick={<DateTick activeDate={activeDate} />}
          tickLine={false}
          tickMargin={10}
        />
        <YAxis
          allowDecimals={false}
          axisLine={{ stroke: AXIS_COLOUR }}
          tick={<CountTick />}
          tickLine={false}
          width={38}
        />
        <Tooltip
          content={<EngagementTooltip />}
          cursor={{ stroke: '#D0D5DD', strokeDasharray: '3 3' }}
        />
        {engagement.series.map(({ dataRoomId, name }, index) => (
          <Area
            activeDot={{ fill: seriesColour(index).line, r: 3.5, stroke: 'white', strokeWidth: 1 }}
            dataKey={seriesKey(dataRoomId)}
            dot={false}
            fill={`url(#${gradientPrefix}-${seriesKey(dataRoomId)})`}
            key={seriesKey(dataRoomId)}
            name={name}
            stroke={seriesColour(index).line}
            strokeWidth={2}
            type="linear"
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
};

/**
 * The chart in its card: title, what it counts, and — once there is more than one line to tell
 * apart — a legend entry per room it draws.
 */
export const EngagementPanel: FC<{ engagement: DataRoomEngagement }> = ({ engagement }) => (
  <div className="flex flex-col gap-8 rounded-md bg-white p-6 shadow-sm">
    <div className="flex items-center justify-between gap-4">
      <div className="flex w-fit flex-col border-b border-dashed border-gray-200 pb-2">
        <span className="text-sm font-[550] leading-5 text-gray-700">
          <AppFormattedMessage id="ENGAGEMENT_OVER_TIME" />
        </span>
        <span className="text-label-sm font-medium text-gray-500">
          <AppFormattedMessage id="DOCUMENT_VIEWS_PER_DAY" />
        </span>
      </div>
      {engagement.series.length > 1 && (
        <div className="flex flex-wrap items-center justify-end gap-3">
          {engagement.series.map(({ dataRoomId, name }, index) => (
            <div className="flex items-center gap-2" key={seriesKey(dataRoomId)}>
              <span
                className="size-2 shrink-0 rounded-full"
                style={{ backgroundColor: seriesColour(index).line }}
              />
              <span className="text-label-md font-medium text-gray-700">{name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
    <EngagementChart engagement={engagement} />
  </div>
);
